/**
 * CreateTableSidePanel Component
 *
 * Side panel version of the table creation wizard.
 * Renders as an inline panel in the AppShell instead of a Sheet overlay.
 *
 * @module features/workspace/components/CreateTableSheet
 */

import { X, Check } from 'lucide-react';
import { Button, Progress } from '@loader/ui';

import { UploadStep, ConfigureStep, ReviewStep } from './steps';
import { useCreateTableFlow } from './hooks';
import { useCreateTableStore } from '../../stores/useCreateTableStore';

// ============================================================================
// STEP CONFIGURATION
// ============================================================================

const STEPS = [
  { id: 'upload', title: 'Upload', description: 'Name and upload file' },
  { id: 'configure', title: 'Configure', description: 'Customize fields' },
  { id: 'review', title: 'Review', description: 'Create table' },
] as const;

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface StepIndicatorProps {
  stepNumber: number;
  totalSteps: number;
  progress: number;
}

/**
 * Step indicator with progress bar
 */
function StepIndicator({ stepNumber, totalSteps, progress }: StepIndicatorProps) {
  return (
    <div className="space-y-3 px-4 pb-4 border-b">
      {/* Progress text */}
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">
          Step {stepNumber} of {totalSteps}
        </span>
      </div>

      {/* Progress bar */}
      <Progress value={progress} className="h-1.5" />

      {/* Step dots */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const stepNum = index + 1;
          const isCompleted = stepNum < stepNumber;
          const isCurrent = stepNum === stepNumber;

          return (
            <div key={step.id} className="flex items-center gap-2">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                  isCompleted || isCurrent
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {isCompleted ? (
                  <Check className="h-3 w-3" />
                ) : (
                  stepNum
                )}
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-medium">{step.title}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export interface CreateTableSidePanelProps {
  onClose: () => void;
}

/**
 * CreateTableSidePanel component
 *
 * Renders the create table wizard as an inline side panel.
 */
export function CreateTableSidePanel({ onClose }: CreateTableSidePanelProps) {
  // Flow hook for handlers and upload state
  const {
    stepNumber,
    totalSteps,
    progress,
    folderName,
    // Upload step state
    isUploading,
    versionStatus,
    versionProgress,
    errorMessage,
    // Step handlers
    handleUpload,
    handleNextAfterUpload,
    isPreparingNext,
    handleRetry,
    handleConfigure,
    handleCreate,
    handleBack,
    // Storage limit dialog
    StorageLimitDialog,
  } = useCreateTableFlow();

  // Store for form state
  const {
    currentStep,
    tableName,
    setTableName,
    nameExists,
    file,
    setFile,
    fileName,
    fileSize,
    sheetName,
    setSheetName,
    fields,
    updateField,
    isSavingFields,
    isFinalizing,
  } = useCreateTableStore();

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <h2 className="text-lg font-semibold">Create Table</h2>
          <p className="text-sm text-muted-foreground">
            Create a new table from a data file
          </p>
        </div>
        <Button variant="ghost" size="icon" className="size-8" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      {/* Step indicator */}
      <StepIndicator
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        progress={progress}
      />

      {/* Step content - each step handles its own scrolling */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {currentStep === 'upload' && (
          <UploadStep
            tableName={tableName}
            onTableNameChange={setTableName}
            nameExists={nameExists}
            file={file}
            onFileSelect={setFile}
            storedFileName={fileName}
            isUploading={isUploading}
            versionStatus={versionStatus}
            versionProgress={versionProgress}
            errorMessage={errorMessage}
            onUpload={handleUpload}
            onRetry={handleRetry}
            folderName={folderName ?? undefined}
            onSheetChange={setSheetName}
            selectedSheet={sheetName}
            onNext={handleNextAfterUpload}
            isPreparingNext={isPreparingNext}
          />
        )}

        {currentStep === 'configure' && (
          <ConfigureStep
            fields={fields}
            onUpdateField={updateField}
            isSaving={isSavingFields}
            onContinue={handleConfigure}
            onBack={handleBack}
          />
        )}

        {currentStep === 'review' && (
          <ReviewStep
            tableName={tableName}
            fileName={file?.name ?? fileName ?? ''}
            fileSize={file?.size ?? fileSize ?? 0}
            fieldCount={fields.length}
            folderName={folderName ?? undefined}
            isCreating={isFinalizing}
            onCreate={handleCreate}
            onBack={handleBack}
          />
        )}
      </div>

      {/* Storage Limit Dialog - rendered by the hook */}
      <StorageLimitDialog />
    </div>
  );
}
